import * as React from 'react';
import * as styles from './navigation.module.scss';
import { CircularProgress } from '@material-ui/core';
import NavList from './navList';
import useNavigation from '../../../../hooks/useNavigation';
import useTimedAlerts from '../../../../hooks/useTimedAlerts';
import PriorityTwoFailure from '../../../announcement/priority-two-failure';
import PriorityTwoSuccess from '../../../announcement/priority-two-success';
import Navigation from '../../../../models/navigation';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import AddNavigation from './addNavigation/addNavigation';
import useDashboardManagement from '../../../../hooks/useDashboardManagement';
import Dashboard from '../../../../models/dashboard';

interface Props {
  clientId: number;
}

const ClientNavigation = ({ clientId }: Props) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingDashboards, setLoadingDashboards] = React.useState<boolean>(true);
  const [navigations, setNavigations] = React.useState<Navigation[]>([]);
  const [clientDashboards, setClientDashboards] = React.useState<Dashboard[]>([]);
  const { successMessage, setSuccessMessage, errorMessage, setErrorMessage } = useTimedAlerts();
  const { fetchNavigations, updateNavigations, deleteNavigation } = useNavigation();
  const { fetchAllDashboards } = useDashboardManagement();
  const scrollRef = React.useRef(null);
  let prevNavs: Navigation[] = [];

  const updateNavigationsHandler = (data: Navigation[]) => {
    updateNavigations(clientId, data)
      .then(response => {
        if (response.success) {
          setSuccessMessage('Navigation updated successfully');
        } else {
          setErrorMessage(response.message);
          setNavigations(prevNavs);
        }
      })
      .catch(err => {
        setErrorMessage(err.responseJSON ? err.responseJSON.message : 'Request failed');
        setNavigations(prevNavs);
      });
  };

  const updateDashboardNavigation = (newDashboard: Navigation) => {
    const updatedNavigations = navigations.map(nav => {
      if (nav.navigationId === newDashboard.parentId) {
        const updatedChildren = nav.children.map(child => {
          if (child.navigationId === newDashboard.navigationId) {
            return newDashboard;
          }
          return child;
        });
        return { ...nav, children: updatedChildren };
      }
      return nav;
    });
    prevNavs = navigations;
    setNavigations(updatedNavigations);
    updateNavigationsHandler(updatedNavigations);
  };

  const updateCategoryNavigation = (newCategory: Navigation) => {
    const updatedNavigations = navigations.map(nav => {
      if (nav.navigationId === newCategory.navigationId) {
        return newCategory;
      }
      return nav;
    });
    prevNavs = navigations;
    setNavigations(updatedNavigations);
    updateNavigationsHandler(updatedNavigations);
  };

  const deleteCategory = (navigationId: number) => {
    const newNavigations = navigations
      .filter(nav => nav.navigationId !== navigationId)
      .map((navigation, i) => {
        navigation.seqOrder = i;
        return navigation;
      });
    deleteNavigation(clientId, navigationId)
      .then(response => {
        if (response.success) {
          setSuccessMessage(`Dashboard Category ${response.message} removed`);
          setNavigations(newNavigations);
        } else {
          setErrorMessage(response.message);
        }
      })
      .catch(err => {
        setErrorMessage(err.responseJSON ? err.responseJSON.message : 'Request failed');
      });
  };

  const deleteDashboard = (dashboard: Navigation) => {
    const navigationId = dashboard.navigationId;
    const parentNav = navigations.find(navigation => navigation.navigationId === dashboard.parentId);
    const newChildren = parentNav.children
      .filter(child => child.navigationId !== navigationId)
      .map((child, i) => {
        child.seqOrder = i;
        return child;
      });
    const newParent = {
      ...parentNav,
      children: newChildren
    };
    deleteNavigation(clientId, navigationId)
      .then(response => {
        if (response.success) {
          setSuccessMessage(`Dashboard ${response.message} removed`);
          setNavigations(
            navigations.map(navigation => {
              if (navigation.navigationId === parentNav.navigationId) {
                return newParent;
              }
              return navigation;
            })
          );
        } else {
          setErrorMessage(response.message);
        }
      })
      .catch(err => {
        setErrorMessage(err.responseJSON ? err.responseJSON.message : 'Request failed');
      });
  };

  React.useEffect(() => {
    fetchNavigations(clientId)
      .then(fetchedNavs => {
        if (fetchedNavs) {
          // standardize seqOrder values in case of old data in both parents and children
          const orderednavs = fetchedNavs.map((navigation, index) => ({ ...navigation, seqOrder: index }));
          orderednavs.forEach(nav => {
            if (nav.children) {
              nav.children = nav.children.map((child, index) => ({ ...child, seqOrder: index }));
            }
          });
          setNavigations(orderednavs);
          setLoading(false);
        }
      })
      .catch(err => {
        setErrorMessage(err.responseJSON.message);
      });
    fetchAllDashboards(clientId)
      .then(response => {
        setClientDashboards(response.results);
        setLoadingDashboards(false);
      })
      .catch(err => {
        setErrorMessage(err.responseJSON.message);
        setLoadingDashboards(false);
      });
  }, []);

  const onDragEnd = (result: DropResult) => {
    const { destination, source, type } = result;

    // user cancels interaction (i.e. drags off the droppable area)
    if (!destination) {
      return;
    }
    // user dropped item in the same location it was in
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    let updatedNavigations = [];
    if (type === 'CATEGORY') {
      // if top-level navigation category is dragged
      const nextNavs = Array.from(navigations);
      const [moved] = nextNavs.splice(source.index, 1);
      nextNavs.splice(destination.index, 0, moved);

      updatedNavigations = nextNavs.map((nav, i) => ({ ...nav, seqOrder: i }));
    } else if (type === 'DASHBOARD') {
      // if dashboard is dragged
      const sourceParent = navigations.find(navigation => navigation.navigationId === parseInt(source.droppableId));
      const destinationParent = navigations.find(
        navigation => navigation.navigationId === parseInt(destination.droppableId)
      );

      if (destination.droppableId === source.droppableId) {
        // if dashboard is dropped within its original parent category
        const nextDashes = Array.from(sourceParent.children);
        const [moved] = nextDashes.splice(source.index, 1);
        nextDashes.splice(destination.index, 0, moved);
        // update dashboard list seqOrders
        const updatedDashes = nextDashes.map((dash, i) => ({ ...dash, seqOrder: i }));
        // update parent navigation with new dashboard
        updatedNavigations = navigations.map(nav => {
          if (nav.navigationId === parseInt(destination.droppableId)) {
            return { ...nav, children: updatedDashes };
          }
          return nav;
        });
      } else {
        // if dashboard is dropped on a different parent
        const nextSource = Array.from(sourceParent.children);
        const nextDest = Array.from(destinationParent.children);
        const [moved] = nextSource.splice(source.index, 1);
        // update parent id of dragged element
        moved.parentId = destinationParent.navigationId;
        nextDest.splice(destination.index, 0, moved);
        // update dashboard list seqOrders
        const updatedSource = nextSource.map((dash, i) => ({ ...dash, seqOrder: i }));
        const updatedDest = nextDest.map((dash, i) => ({ ...dash, seqOrder: i }));
        // update parent navigation with new dashboard
        updatedNavigations = navigations.map(nav => {
          if (nav.navigationId === parseInt(source.droppableId)) {
            return { ...nav, children: updatedSource };
          } else if (nav.navigationId === parseInt(destination.droppableId)) {
            return { ...nav, children: updatedDest };
          }
          return nav;
        });
      }
    }
    // store fallback in case update fails
    prevNavs = navigations;
    setNavigations(updatedNavigations);
    updateNavigationsHandler(updatedNavigations);
  };

  return (
    <div className={styles.navigation}>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      {successMessage && <PriorityTwoSuccess message={successMessage} />}
      <AddNavigation
        clientId={clientId}
        navigations={navigations}
        setNavigations={setNavigations}
        setSuccessMessage={setSuccessMessage}
        setErrorMessage={setErrorMessage}
        loadingDashboards={loadingDashboards}
        clientDashboards={clientDashboards}
        scrollRef={scrollRef}
      />
      <div className={styles.navListWrap}>
        {loading ? (
          <div className="loadingIndicator">
            <CircularProgress />
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <NavList
              navigations={navigations}
              clientDashboards={clientDashboards}
              updateDashboardNavigation={updateDashboardNavigation}
              updateCategoryNavigation={updateCategoryNavigation}
              deleteCategory={deleteCategory}
              deleteDashboard={deleteDashboard}
              scrollRef={scrollRef}
            />
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default ClientNavigation;
